import {SliderItem} from "../../../../model/items/slider";
import m, {Vnode, VnodeDOM} from "mithril";
import {LabelItem} from "../../../../model/items/labelItem";
import {Questionary} from "../../../../model/questionary";

/**      Slider box
 * """""""""""""""""""""""""
 *
 *                  7
 * ================(o)======
 * |     '     |     '     |
 * Min                   Max
 *
 */

export default class SliderBox {

    private readonly questionary: Questionary;

    private boxContainer: JQuery|null = null;
    private focused: boolean = false;
    private inputFocused: boolean = false;

    private onChange?: (value: number)=>void;

    private static readonly axisBluePrints = [
        [1,0,1,0,1],    // default
        [1],
        [1,1],
        [1,1,1],
        [1,1,1,1],
        [1,0,1,0,1],
        [1,0,0,0,0,1],
        [1,0,0,1,0,0,1],
        [1,0,0,0,0,0,0,1],
        [1,0,1,0,1],
        [1,0,0,0,0,0,0,0,0,1],
        [1,0,0,0,0,1,0,0,0,0,1],
    ];

    constructor(
        public readonly sliderItem: SliderItem,
        private readonly minLabel: string|null,
        private readonly maxLabel: string|null,
        public type: "numeric"|"text"|"auto"|"numeric-labeled" = "numeric",
        private readonly image?: string
    ) {
        this.questionary = sliderItem.questionary;
        if (this.type === "auto") {
            this.type = 'numeric'; // BC
        }
    }

    public setOnChange(callback?: (value: number)=>void): SliderBox {
        this.onChange = callback;
        return this;
    }

    public get value() {
        return this.sliderItem.getVariable();
    }

    private hasValue() {
        return this.value !== undefined;
    }

    private getLabelText(label: string|null): string {
        if (!label) {
            return '';
        }
        return this.questionary.renderText(label);
    }

    private onPointerDown(e: PointerEvent) {
        if (e.button !== 0) {
            return;
        }
        this.focused = true;
        (<HTMLElement>e.currentTarget).setPointerCapture(e.pointerId);
        this.setSliderPosition( SliderBox.getPercentFromEvent(e) );
    }
    private onPointerMove(e: PointerEvent) {
        if (!this.focused) {
            return;
        }
        e.stopPropagation();
        e.preventDefault();
        this.setSliderPosition( SliderBox.getPercentFromEvent(e) );
    }
    private onPointerUp(e: PointerEvent) {
        this.focused = false;
        (<HTMLElement>e.currentTarget).releasePointerCapture(e.pointerId);
    }

    private onMouseClick(e: MouseEvent) {
        this.setSliderPosition( SliderBox.getPercentFromEvent(e) );
    }

    private static getPercentFromEvent(e: MouseEvent|TouchEvent): number {
        e.preventDefault();
        const $el = $(<HTMLElement>(e.currentTarget));
        const width = $el.width();
        const elX = $el.offset().left;
        const clickX = e instanceof MouseEvent ? e.pageX : e.touches[0].clientX;
        return (clickX-elX)/width*100;
    }


    private set sliderValue(v: number) {
        this.sliderItem.set(v);
        this.onChange?.(v);
    }

    private get sliderValue(): number {
        const value = this.sliderItem.getVariable();
        return value ? Number(value) : 0;
    }

    /**
     * Get string value of slider, that should be displayed to user
     */
    private get sliderLabeledValue(): string {
        const value = this.sliderValue;
        if (this.type === "numeric-labeled") {
            if (this.minLabel && this.sliderItem.range.min === value) {
                return this.getLabelText(this.minLabel);
            } else if (this.maxLabel && this.sliderItem.range.max === value) {
                return this.getLabelText(this.maxLabel);
            }
        }
        return String(value);
    }

    /** Slider position in percent based on value */
    private getSliderPosition(): number {
        const value = this.hasValue() ? this.sliderValue : (this.sliderItem.initialValue ?? 50);

        const min = this.sliderItem.range.min;
        const max = this.sliderItem.range.max;
        return Math.max(0, Math.min(100, (value - min)/(max-min)*100 ));
    }

    private setSliderPosition(percent: number) {
        percent = Math.max(0, Math.min(100, percent));
        const min = this.sliderItem.range.min;
        const max = this.sliderItem.range.max;
        this.sliderValue = Math.round((max-min)*percent/100) + min;
    }

    private getAxisWidth() {
        return this.hasValue() ? this.getSliderPosition() : 0;
    }

    // Compute scale for min/max value under axis
    private getScale(whichValue: "min"|"max") {
        if (this.type === "numeric" || this.type === "numeric-labeled") {
            return 1;
        }
        const position = this.getSliderPosition();
        const scaleForMaxValue = 6/60625*position*position + 1/194*position + 48/97;
        return whichValue === "min" ? 1/scaleForMaxValue : scaleForMaxValue;
    }

    // Is input valid number or void
    private isInputValid(value: string|number|undefined) {
        const number = Number(value);
        return value === undefined || value === '' || (!isNaN(number));
    }

    public view(): Vnode|null {
        if (!this.sliderItem.cell.isVisible()) {
            return null;
        }

        return m('.sliderBox.type-'+this.type+(this.image?'.has-image':'')+(!this.sliderItem.cell.enabled?'.disabled':''), {}, [
            m('.sliderBox-value'+(this.hasValue()?'.set':''), {},
                (this.type === "numeric" || this.type === "numeric-labeled") ? this.viewInput() : m('span', {}, this.sliderLabeledValue)
            ),
            m('.sliderBox-control', {
                onpointerdown: this.onPointerDown.bind(this),
                onpointermove: this.onPointerMove.bind(this),
                onpointerup: this.onPointerUp.bind(this),
                onclick: this.onMouseClick.bind(this),
            }, [
                m('.axis', {}, [
                    m('.filled', {
                        style: {
                            width: this.getAxisWidth()+'%',
                            transition: (this.focused ? 'none' : ''),
                        },
                    }),
                ]),
                m('.slider', {}, [
                    m('.slider-button'+((this.hasValue()?'.set':'')+(this.image?'.has-image':'')), {
                        style: {
                            left: this.getSliderPosition()+'%',
                            transition: (this.focused ? 'none' : ''),
                        },
                    }, [
                        this.image ? m('img', {
                            draggable: false,
                            src: this.image,
                            oncontextmenu: (e: Event) => e.preventDefault(),
                        }) : null,
                    ])
                ]),
            ]),
            m('.sliderBox-info', {}, [
                this.viewInfoAxis(),
                m('.sliderBox-info-values', [
                    m('.values-min', {
                        style: {
                            transform: `scale(${this.getScale("min")}`,
                            transition: (this.focused ? 'none' : ''),
                        },
                    }, m.trust(this.getLabelText(this.minLabel)) ),
                    m('.values-max', {
                        style: {
                            transform: `scale(${this.getScale("max")}`,
                            transition: (this.focused ? 'none' : ''),
                        },
                    }, m.trust(this.getLabelText(this.maxLabel)) ),
                ]),
            ]),

        ]);
    }

    private viewInfoAxis(): Vnode {
        const steps = Math.round((this.sliderItem.range.max - this.sliderItem.range.min) / this.sliderItem.range.step) + 1;
        const bluePrint = SliderBox.axisBluePrints[steps] ? SliderBox.axisBluePrints[steps] : SliderBox.axisBluePrints[0];
        const items: Vnode[] = [];
        for (const i in bluePrint) {
            const v = bluePrint[i];
            const pos = steps===1 ? 50 : (Number(i))/(bluePrint.length-1)*100;
            items.push(
                m('.tick'+(v?'.strong':'.small'), {
                    style: { left: pos+'%' },
                }, [])
             );
        }

        return m('.sliderBox-info-axis', {}, items);
    }

    private viewInput(): Vnode {
        return m('input', {
            value: this.inputFocused ? undefined : (this.hasValue()?this.sliderLabeledValue:''),
            onkeydown: (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    e.preventDefault();
                }
            },
            oninput:  (e: InputEvent) => {
                const value = (<HTMLInputElement>e.currentTarget).value;
                if (value === '') {
                    this.setSliderPosition(50);
                    this.sliderItem.unset();
                } else if (this.isInputValid(value)) {
                    this.sliderValue = Math.max(Math.min(this.sliderItem.range.max, Number(value)), this.sliderItem.range.min)
                    this.setSliderPosition(this.getSliderPosition());
                }
            },
            onfocus: () => this.inputFocused = true,
            onblur: (e: InputEvent) => {
                this.inputFocused = false;
                const el = <HTMLInputElement>e.currentTarget;
                el.value = this.sliderLabeledValue;
            }
        });
    }


    oncreate(vnode: VnodeDOM) {
        this.boxContainer = $(vnode.dom);
    }

}
